import type { Inventory } from '../inventory/inventory.js';

export interface CraftingIngredient {
  itemId: string;
  name: string;
  quantity: number;
}

export interface CraftingResult {
  itemId: string;
  name: string;
  quantity: number;
  unitMassKg?: number;
  maxStack?: number;
}

export interface CraftingRecipe {
  id: string;
  name: string;
  category: 'reagent' | 'equipment' | 'membrane' | 'structure';
  ingredients: readonly CraftingIngredient[];
  result: CraftingResult;
  requiredTraitId?: string;
  craftTimeSec?: number;
}

export const SEED_RECIPES: readonly CraftingRecipe[] = [
  {
    id: 'gel_salve',
    name: 'Gel Salve',
    category: 'reagent',
    ingredients: [{ itemId: 'herb_vitality', name: 'Vitality Herb', quantity: 2 }],
    result: { itemId: 'gel_salve', name: 'Gel Salve', quantity: 1, unitMassKg: 0.3, maxStack: 16 },
    craftTimeSec: 1.0
  },
  {
    id: 'hardened_plate',
    name: 'Hardened Plate',
    category: 'membrane',
    ingredients: [{ itemId: 'iron_ore', name: 'Iron Ore', quantity: 3 }],
    result: { itemId: 'hardened_plate', name: 'Hardened Plate', quantity: 1, unitMassKg: 2.0, maxStack: 8 },
    requiredTraitId: 'iron_membrane',
    craftTimeSec: 2.5
  },
  {
    id: 'crystal_lens',
    name: 'Crystal Focus Lens',
    category: 'equipment',
    ingredients: [
      { itemId: 'mana_crystal', name: 'Mana Crystal', quantity: 2 },
      { itemId: 'iron_ore', name: 'Iron Ore', quantity: 1 }
    ],
    result: { itemId: 'crystal_lens', name: 'Crystal Focus Lens', quantity: 1, unitMassKg: 0.8, maxStack: 4 },
    craftTimeSec: 2.0
  }
];

export class CraftingEngine {
  private recipes = new Map<string, CraftingRecipe>();

  constructor(initialRecipes: readonly CraftingRecipe[] = SEED_RECIPES) {
    for (const r of initialRecipes) {
      this.recipes.set(r.id, r);
    }
  }

  public registerRecipe(recipe: CraftingRecipe): void {
    this.recipes.set(recipe.id, recipe);
  }

  public getRecipe(recipeId: string): CraftingRecipe | undefined {
    return this.recipes.get(recipeId);
  }

  public getAllRecipes(): readonly CraftingRecipe[] {
    return Array.from(this.recipes.values());
  }

  public canCraft(
    recipeId: string,
    inventory: Inventory,
    unlockedTraits: readonly string[] = []
  ): boolean {
    const recipe = this.recipes.get(recipeId);
    if (!recipe) return false;

    // Check trait requirement
    if (recipe.requiredTraitId && !unlockedTraits.includes(recipe.requiredTraitId)) {
      return false;
    }

    // Check all ingredients in inventory
    for (const ing of recipe.ingredients) {
      if (!inventory.hasItem(ing.itemId, ing.quantity)) {
        return false;
      }
    }

    return true;
  }

  public craft(
    recipeId: string,
    inventory: Inventory,
    unlockedTraits: readonly string[] = []
  ): boolean {
    if (!this.canCraft(recipeId, inventory, unlockedTraits)) {
      return false;
    }

    const recipe = this.recipes.get(recipeId)!;

    // Consume ingredients
    for (const ing of recipe.ingredients) {
      inventory.removeItem(ing.itemId, ing.quantity);
    }

    // Deliver crafted output item to inventory
    const leftover = inventory.addItem({
      itemId: recipe.result.itemId,
      name: recipe.result.name,
      quantity: recipe.result.quantity,
      unitMassKg: recipe.result.unitMassKg,
      maxStack: recipe.result.maxStack
    });

    return leftover === 0;
  }
}
